/**
 * Anoura — delivery estimate
 *
 * Renders "Arrives today — order within 3h 12m" from a configurable cutoff
 * time, a set of delivery weekdays, and a per-product lead time.
 *
 * All arithmetic runs in the SHOP's timezone (default Asia/Dubai), never the
 * visitor's: someone browsing from London at 11:00 is already past a 16:00
 * Dubai cutoff. We read the shop-local wall clock via Intl, then rebuild it as
 * a UTC date so day arithmetic can't be knocked sideways by the visitor's own
 * DST rules.
 *
 * Computed client-side on purpose — Shopify caches pages, so a server-rendered
 * countdown would be served stale.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-anoura-delivery]';

  /** Shop-local "now", expressed as a UTC date carrying the shop's wall clock. */
  function zoneNow(timeZone) {
    var formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    var parts = {};
    formatter.formatToParts(new Date()).forEach(function (part) {
      if (part.type !== 'literal') parts[part.type] = part.value;
    });
    var hour = parseInt(parts.hour, 10);
    if (hour === 24) hour = 0; // en-US hour12:false reports midnight as 24
    return new Date(
      Date.UTC(+parts.year, +parts.month - 1, +parts.day, hour, +parts.minute, +parts.second)
    );
  }

  function startOfDay(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  }

  /**
   * First delivery date for an order placed now.
   * @param {boolean} treatAsPastCutoff - force the "ordered after cutoff" branch
   */
  function deliveryDate(now, cutoffMinutes, days, leadDays, treatAsPastCutoff) {
    var minutesNow = now.getUTCHours() * 60 + now.getUTCMinutes();
    var past = treatAsPastCutoff || minutesNow >= cutoffMinutes;
    var date = startOfDay(now);
    var guard = 0;

    if (past) date.setUTCDate(date.getUTCDate() + 1);

    while (days.indexOf(date.getUTCDay()) === -1 && guard++ < 30) {
      date.setUTCDate(date.getUTCDate() + 1);
    }

    var remaining = leadDays;
    while (remaining > 0 && guard < 400) {
      date.setUTCDate(date.getUTCDate() + 1);
      while (days.indexOf(date.getUTCDay()) === -1 && guard++ < 400) {
        date.setUTCDate(date.getUTCDate() + 1);
      }
      remaining--;
    }
    return date;
  }

  function dateLabel(target, today, locale, todayLabel, tomorrowLabel) {
    var diff = Math.round((target.getTime() - today.getTime()) / 86400000);
    if (diff === 0) return todayLabel;
    if (diff === 1) return tomorrowLabel;
    try {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }).format(target);
    } catch (e) {
      return target.toDateString();
    }
  }

  function countdownLabel(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    var hours = Math.floor(total / 3600);
    var minutes = Math.floor((total % 3600) / 60);
    var seconds = total % 60;
    if (hours > 0) return hours + 'h ' + minutes + 'm';
    if (minutes > 0) return minutes + 'm';
    return seconds + 's';
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  function render(el) {
    var target = el.querySelector('[data-delivery-text]');
    if (!target) return;

    var days = (el.dataset.days || '')
      .split(',')
      .filter(function (value) { return value !== ''; })
      .map(Number);

    if (!days.length) {
      el.hidden = true;
      return;
    }

    var timeZone = el.dataset.timezone || 'Asia/Dubai';
    var cutoff = parseInt(el.dataset.cutoff, 10) || 0;
    var lead = parseInt(el.dataset.lead, 10) || 0;
    var locale = el.dataset.locale || 'en';

    var now;
    try {
      now = zoneNow(timeZone);
    } catch (e) {
      el.hidden = true; // invalid timezone string — fail quiet rather than lie
      return;
    }

    var today = startOfDay(now);
    var minutesNow = now.getUTCHours() * 60 + now.getUTCMinutes();
    var pastCutoff = minutesNow >= cutoff;

    var dateIfOrderedNow = deliveryDate(now, cutoff, days, lead, false);
    var dateIfMissed = deliveryDate(now, cutoff, days, lead, true);

    // Only nudge when beating the cutoff actually buys an earlier date.
    var showCountdown = !pastCutoff && dateIfOrderedNow.getTime() !== dateIfMissed.getTime();

    var template = showCountdown ? el.dataset.tplBefore : el.dataset.tplAfter;
    var html = escapeHtml(template || '').replace(
      '{date}',
      '<strong class="anoura-delivery__date">' +
        escapeHtml(dateLabel(dateIfOrderedNow, today, locale, el.dataset.labelToday || 'today', el.dataset.labelTomorrow || 'tomorrow')) +
        '</strong>'
    );

    if (showCountdown) {
      var cutoffAt = today.getTime() + cutoff * 60000;
      html = html.replace(
        '{countdown}',
        '<strong class="anoura-delivery__countdown">' + escapeHtml(countdownLabel(cutoffAt - now.getTime())) + '</strong>'
      );
    } else {
      html = html.replace('{countdown}', '');
    }

    if (target.innerHTML !== html) target.innerHTML = html;
    el.hidden = false;
  }

  function tick() {
    document.querySelectorAll(SELECTOR).forEach(render);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tick);
  } else {
    tick();
  }

  setInterval(tick, 1000);
  document.addEventListener('shopify:section:load', tick);
  document.addEventListener('shopify:block:select', tick);

  /**
   * Shared date maths, reused by the cart delivery scheduler so both features
   * agree on cutoff, delivery weekdays and lead time.
   */
  window.AnouraDelivery = {
    zoneNow: zoneNow,
    startOfDay: startOfDay,
    deliveryDate: deliveryDate,
    dateLabel: dateLabel,
    countdownLabel: countdownLabel,
  };
})();
